import { Typography } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import {
  type NavigateFunction,
  Outlet,
  useNavigate,
  useParams
} from 'react-router-dom'

import { isKubernetes } from 'components/attacks/create/targets/target-utils'
import {
  PanelHeader,
  PanelTitle,
  PanelDetails
} from 'components/attacks/details/AttackStyles'
import Section from 'components/common/Section'
import { ConcurrentExecutionNodeOffCanvasOutletContext } from 'components/scenarios/concurrent/ConcurrentExecutionNodeOffCanvas'
import { ConcurrentObserversTreeView } from 'components/scenarios/concurrent/ConcurrentObserversTreeView'
import { ConcurrentTreeView } from 'components/scenarios/concurrent/ConcurrentTreeView'
import { GraphEditorContextProvider } from 'components/scenarios/concurrent/GraphEditorContext'
import {
  findRunStep,
  generateGraphNodePathMap,
  isContinuousStatusCheckNode,
  isInfraAttackNode,
  isFailureFlagNode,
  isSynchronousStatusCheckNode
} from 'components/scenarios/utils/graph'
import { StatusChecksProvider } from 'context/status-checks/StatusChecksContext'
import { useTranslation } from 'hooks/use-translation'
import theme from 'theme'

import type { ScenarioPreview } from 'types/api/reliability-tests/preview'
import type { ScenarioRun } from 'types/api/scenarios/runs/scenario-runs'
import type {
  FailureFlagNode,
  GraphNode,
  InfraAttackNode,
  Scenario,
  SynchronousStatusCheckNode
} from 'types/api/scenarios/scenario'

function isSelectedNodeAStatusCheck(
  node: GraphNode | undefined
): node is SynchronousStatusCheckNode {
  return node
    ? Boolean(
        isSynchronousStatusCheckNode(node) || isContinuousStatusCheckNode(node)
      )
    : false
}

export const ConcurrentExecution: React.FC<{
  run?: ScenarioRun
  scenario: Pick<Scenario, 'graph'>
  scenarioPreview?: ScenarioPreview
  showTitle?: boolean
  onAttackDetailsOpen?: (node: GraphNode, navigate: NavigateFunction) => void
  onAttackDetailsClose?: (node: GraphNode, navigate: NavigateFunction) => void
}> = ({
  run,
  scenario,
  scenarioPreview,
  showTitle,
  onAttackDetailsOpen,
  onAttackDetailsClose
}) => {
  const { trans } = useTranslation()
  const navigate = useNavigate()
  const { nodeId, step } = useParams<{ nodeId: string; step: string }>()
  const nodeFromRouter = nodeId ?? step
  const graph = run?.graph ?? scenario?.graph
  const graphNodePaths = generateGraphNodePathMap(graph)
  const selectedNode =
    run && nodeFromRouter !== undefined
      ? findRunStep(run, nodeFromRouter, {})
      : undefined
  const isSelectedNodeAnAttack = isInfraAttackNode(selectedNode)
  const isSelectedNodeAKubernetesAttack = isKubernetes(
    selectedNode?.attack_configuration?.targets[0]?.targetingStrategy?.type
  )

  const [selectedSummary, setSelectedSummary] = useState<
    InfraAttackNode | undefined
  >(
    isSelectedNodeAnAttack && !isSelectedNodeAKubernetesAttack
      ? selectedNode
      : undefined
  )

  const [selectedKubernetesSummary, setSelectedKubernetesSummary] = useState<
    InfraAttackNode | undefined
  >(isSelectedNodeAKubernetesAttack ? selectedNode : undefined)

  const [selectedStatusCheckSummary, setSelectedStatusCheckSummary] = useState<
    SynchronousStatusCheckNode | undefined
  >(isSelectedNodeAStatusCheck(selectedNode) ? selectedNode : undefined)

  const [selectedFailureFlagNode, setSelectedFailureFlagNode] = useState<
    FailureFlagNode | undefined
  >(isFailureFlagNode(selectedNode) ? selectedNode : undefined)

  const handleOnInfraAttackNodeClick = useCallback((node: InfraAttackNode) => {
    node.attack_configuration?.targets[0] &&
    isKubernetes(node.attack_configuration.targets[0].targetingStrategy?.type)
      ? setSelectedKubernetesSummary(node)
      : setSelectedSummary(node)
  }, [])

  const clearNodeState = useCallback(() => {
    setSelectedSummary(undefined)
    setSelectedKubernetesSummary(undefined)
    setSelectedStatusCheckSummary(undefined)
    setSelectedFailureFlagNode(undefined)

    onAttackDetailsClose?.(selectedNode, navigate)
  }, [selectedNode, navigate, onAttackDetailsClose])

  const handleSelectNode = useCallback(
    (node: GraphNode | FailureFlagNode) => {
      if (isFailureFlagNode(node)) {
        setSelectedFailureFlagNode(node)
      }

      if (isInfraAttackNode(node)) {
        handleOnInfraAttackNodeClick(node)
      } else if (isSelectedNodeAStatusCheck(node)) {
        setSelectedStatusCheckSummary(node)
      } else if (isFailureFlagNode(node)) {
        setSelectedFailureFlagNode(node)
      } else {
        /* eslint-disable-next-line no-console */
        console.warn('Unknown node type clicked in ConcurrentExecution:', node)
      }

      onAttackDetailsOpen?.(node, navigate)
    },
    [handleOnInfraAttackNodeClick, navigate, onAttackDetailsOpen]
  )

  const contextValue = useMemo<ConcurrentExecutionNodeOffCanvasOutletContext>(
    () => ({
      run,
      selectedFailureFlagNode,
      scenarioPreview,
      selectedSummary,
      selectedKubernetesSummary,
      selectedStatusCheckSummary,
      onClose: clearNodeState
    }),
    [
      run,
      selectedFailureFlagNode,
      scenarioPreview,
      selectedKubernetesSummary,
      selectedStatusCheckSummary,
      selectedSummary,
      clearNodeState
    ]
  )

  return (
    <StatusChecksProvider>
      <Section data-test-scenario-details-attack-configuration>
        {run?.stage_info?.stageMetadata?.failedReason && (
          <Typography color="error">
            {run.stage_info.stageMetadata.failedReason}
          </Typography>
        )}
        {showTitle && (
          <PanelHeader>
            <PanelTitle>{trans('scenario_execution')}</PanelTitle>
          </PanelHeader>
        )}
        <PanelDetails css={{ marginTop: theme.space[0] }}>
          <GraphEditorContextProvider
            graph={graph}
            graphNodePaths={graphNodePaths}
          >
            {Boolean(graph?.observers?.length) && (
              <ConcurrentObserversTreeView
                css={{ marginBottom: theme.space[2] }}
                graph={graph}
                isClickable={Boolean(run)}
                onClick={handleSelectNode}
              />
            )}
            <ConcurrentTreeView
              hasRun={Boolean(run)}
              isClickable={Boolean(run)}
              scenarioPreview={scenarioPreview}
              onClick={handleSelectNode}
            />
          </GraphEditorContextProvider>
          {/* Node Details Drawer */}
          <Outlet context={contextValue} />
        </PanelDetails>
      </Section>
    </StatusChecksProvider>
  )
}
